import {
  Modal,
  Box,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Textarea,
  Spinner,
  Alert,
} from "@chakra-ui/react";
import CategoryMultiSelect from "components/CategoryMultiSelect";
import LanguageMultiSelect from "components/LanguageMultiSelect";
import { putRequest, useMutationWrapper } from "services/apiHelper";
import { convertParamsToString } from "helpers/stringManipulation";
import { scriptsRequest } from "services/request";
import { QUERY_KEYS, type ScriptType, statusOBJ } from "utils/constants";
import { errorToast, successToast } from "utils/hooks";
import { BsCheckCircle } from "react-icons/bs";
import { RiErrorWarningFill } from "react-icons/ri";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { PROTECTED_PATHS } from "routes/pagePath";
import { getRequestError } from "utils/requestError";
import PublicationReadiness from "./PublicationReadiness";
import { useEffect, useState } from "react";
import axiosInstance from "services/httpService";
import {
  isCategoryFieldEditable,
  isLanguageFieldEditable,
  buildVerifyPayloadFields,
} from "utils/scriptVerification";

interface VerifyModalProps {
  isOpen: boolean;
  onClose: () => void;
  isApproved: boolean;
  script: ScriptType;
  onVerified?: (updatedScript: ScriptType) => void;
}

interface ClassifyPrediction {
  category: string;
  probability: number;
}

const VerifyModal = (props: VerifyModalProps) => {
  const { isOpen, onClose, isApproved = true, script, onVerified } = props;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<any>(null);
  const [loadError, setLoadError] = useState("");
  const [approvalError, setApprovalError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [reason, setReason] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [customCategory, setCustomCategory] = useState("");
  const [usesOtherCategory, setUsesOtherCategory] = useState(false);
  const [categoryNeedsReview, setCategoryNeedsReview] = useState(false);
  const [selectedLanguageIds, setSelectedLanguageIds] = useState<string[]>([]);
  const [legacyLanguage, setLegacyLanguage] = useState("");
  const [usesOtherLanguage, setUsesOtherLanguage] = useState(false);
  const [languageNeedsReview, setLanguageNeedsReview] = useState(false);
  const [hasExistingCategoryLinks, setHasExistingCategoryLinks] = useState(false);
  const [hasExistingLanguageLinks, setHasExistingLanguageLinks] = useState(false);
  const [classifySuggestions, setClassifySuggestions] = useState<ClassifyPrediction[]>([]);
  const [isLoadingSuggestion, setIsLoadingSuggestion] = useState(false);

  useEffect(() => {
    if (!isOpen || !script?.id) return;
    let active = true;
    setDetail(null);
    setReason("");
    setLoadError("");
    setApprovalError("");

    setSelectedCategory(script.category ?? "All");
    setSelectedCategoryIds((script.categories ?? []).map((category) => category.id));
    setCustomCategory(script.customCategory ?? "");
    setUsesOtherCategory(script.category === "others");
    setCategoryNeedsReview(false);
    setHasExistingCategoryLinks((script.categories ?? []).length > 0);
    setSelectedLanguageIds((script.languages ?? []).map((language) => language.id));
    setLegacyLanguage(script.language ?? "");
    setUsesOtherLanguage(false);
    setLanguageNeedsReview(false);
    setHasExistingLanguageLinks((script.languages ?? []).length > 0);
    setClassifySuggestions([]);
    setIsLoadingSuggestion(true);

    const fetchScriptSuggestion = async () => {
      try {
        const url = convertParamsToString(scriptsRequest.SCRIPT, { id: script.id });
        const response = await axiosInstance.get(url);
        if (!active) return;
        const scriptDetail = response.data?.data;
        if (!scriptDetail?.id || scriptDetail.id !== script.id)
          throw new Error("Invalid script detail");
        setDetail(scriptDetail);

        setSelectedCategory(scriptDetail.category ?? "All");
        setSelectedCategoryIds((scriptDetail.categories ?? []).map((category) => category.id));
        setCustomCategory(scriptDetail.customCategory ?? "");
        setUsesOtherCategory(scriptDetail.category === "others");
        setCategoryNeedsReview(
          Boolean(response.data?.categoryNeedsReview || scriptDetail.categoryNeedsReview),
        );
        setHasExistingCategoryLinks((scriptDetail.categories ?? []).length > 0);
        setSelectedLanguageIds((scriptDetail.languages ?? []).map((language) => language.id));
        setLegacyLanguage(scriptDetail.language ?? "");
        setUsesOtherLanguage(false);
        setLanguageNeedsReview(
          Boolean(response.data?.languageNeedsReview || scriptDetail.languageNeedsReview),
        );
        setHasExistingLanguageLinks((scriptDetail.languages ?? []).length > 0);
        setClassifySuggestions(response.data?.classifySuggestion?.predictions ?? []);
      } catch (error) {
        if (active)
          setLoadError(
            getRequestError(
              error,
              "Unable to load current script details. Retry before approving.",
            ),
          );
      } finally {
        if (active) setIsLoadingSuggestion(false);
      }
    };

    void fetchScriptSuggestion();
    return () => {
      active = false;
    };
  }, [isOpen, script, attempt]);

  const onSuccess = (response) => {
    void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ADMIN_SCRIPTS] });

    onClose();
    onVerified?.(response?.data ?? script);
    successToast(isApproved ? `${script.name} has been published..👏🏻 ` : "Script rejected");
  };
  const { APPROVED, REJECTED } = statusOBJ;
  const { mutate, isLoading } = useMutationWrapper(putRequest, onSuccess, (error) => {
    setApprovalError(getRequestError(error, "Unable to verify this script. Please try again."));
  });
  const categoryEditable = isCategoryFieldEditable({
    hasExistingCategoryLinks,
    categoryNeedsReview,
  });
  const languageEditable = isLanguageFieldEditable({
    hasExistingLanguageLinks,
    languageNeedsReview,
  });
  const onLanguageChange = ({ languageIds, language, usesOtherLanguage }) => {
    setSelectedLanguageIds(languageIds);
    setLegacyLanguage(language);
    setUsesOtherLanguage(usesOtherLanguage);
  };
  const onCategoryChange = ({ categoryIds, category, customCategory, usesOtherCategory }) => {
    setSelectedCategoryIds(categoryIds);
    setSelectedCategory(category);
    setCustomCategory(customCategory);
    setUsesOtherCategory(usesOtherCategory);
  };
  const handleScriptVerification = () => {
    if (isLoading || (isApproved && (!detail || isLoadingSuggestion || loadError))) return;
    setApprovalError("");
    if (isApproved && selectedCategoryIds.length === 0) {
      errorToast("Please select a catalogue category before approving");
      return;
    }
    if (isApproved && selectedLanguageIds.length === 0) {
      errorToast("Please select a catalogue language before approving");
      return;
    }

    const url = convertParamsToString(scriptsRequest.SCRIPTS_VERIFY, { id: script.id });

    mutate({
      url,
      data: {
        status: isApproved ? APPROVED : REJECTED,
        composerId: detail?.composerId ?? script.composerId,
        category: isApproved ? selectedCategory : script.category,
        others: reason,
        ...(isApproved
          ? buildVerifyPayloadFields({
              categoryEditable,
              languageEditable,
              categoryIds: selectedCategoryIds,
              languageIds: selectedLanguageIds,
            })
          : {}),
      },
    });
  };
  return (
    <Modal
      isOpen={isOpen}
      onClose={isLoading ? () => {} : onClose}
      isCentered
      size="xl"
      closeOnOverlayClick={!isLoading}
    >
      <ModalOverlay />
      <ModalContent alignItems="center">
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Box mt="20px" w="200px" h="100px" ml="70px">
            {isApproved ? (
              <BsCheckCircle size="100px" color="#B5F4AF " />
            ) : (
              <RiErrorWarningFill size="100px" color="#F5582A " />
            )}
          </Box>
          <Text fontSize="20px" fontWeight="800" ml="60px" mt="30px">
            {isApproved ? "Verify" : "Reject"} Script
          </Text>
          <Text mt="30px">
            {isApproved
              ? "Review the score and publication details before approving."
              : "Reject this submission and provide a reason."}
          </Text>
          {approvalError && (
            <Alert status="error" mt={4}>
              {approvalError}
            </Alert>
          )}
          {loadError && (
            <Alert status="error" mt={4}>
              {loadError}
              <Button
                ml={2}
                onClick={() => {
                  setAttempt((value) => value + 1);
                }}
              >
                Retry
              </Button>
            </Alert>
          )}
          {isApproved && detail && (
            <PublicationReadiness
              script={detail}
              categoryNeedsReview={categoryNeedsReview}
              languageNeedsReview={languageNeedsReview}
            />
          )}
          {isApproved && (
            <Button
              mt={3}
              isDisabled={isLoading}
              onClick={() => {
                onClose();
                navigate(
                  `${PROTECTED_PATHS.NEW_EDIT_SCRIPT}?edit=${encodeURIComponent(
                    script.slug || script.id,
                  )}`,
                );
              }}
            >
              Edit script
            </Button>
          )}
          {isApproved && (
            <>
              <Box mt="20px">
                <CategoryMultiSelect
                  category={selectedCategory}
                  categoryIds={selectedCategoryIds}
                  customCategory={customCategory}
                  usesOtherCategory={usesOtherCategory}
                  categoryNeedsReview={categoryNeedsReview}
                  isRequired
                  canCreateCategory={categoryEditable}
                  isDisabled={!categoryEditable}
                  helperText={
                    categoryEditable
                      ? "Confirm every category before approving."
                      : "This script is already linked to a catalogue category. Use Edit script to change it."
                  }
                  onChange={onCategoryChange}
                />
              </Box>
              <Box mt="20px">
                <LanguageMultiSelect
                  language={legacyLanguage}
                  languageIds={selectedLanguageIds}
                  usesOtherLanguage={usesOtherLanguage}
                  isRequired
                  isDisabled={!languageEditable}
                  helperText={
                    languageEditable
                      ? languageNeedsReview
                        ? "This script has a typed language that needs a catalogue match before approval."
                        : "Confirm every language before approving."
                      : "This script is already linked to a catalogue language. Use Edit script to change it."
                  }
                  onChange={onLanguageChange}
                />
              </Box>
            </>
          )}
          <Box
            mt="20px"
            p="3"
            borderWidth="1px"
            borderColor="#dbe3e9"
            borderRadius="md"
            bg="#f8fafc"
          >
            <Text fontWeight="bold" fontSize="sm" color="#4e6e97">
              AI suggestion (low confidence)
            </Text>
            {isLoadingSuggestion ? (
              <Spinner size="sm" mt="2" />
            ) : classifySuggestions.length > 0 ? (
              <Box mt="2">
                {classifySuggestions.slice(0, 3).map((prediction) => (
                  <Text key={prediction.category} fontSize="sm" color="#5f6f7f">
                    {prediction.category} - {Math.round(prediction.probability * 100)}%
                  </Text>
                ))}
              </Box>
            ) : (
              <Text mt="2" fontSize="sm" color="#7f94a6">
                No suggestion available. Please review manually.
              </Text>
            )}
          </Box>
          <Textarea
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
            placeholder="Enter reason..."
            size="lg"
            mt="10px"
          />
        </ModalBody>
        <ModalFooter>
          <Button
            onClick={handleScriptVerification}
            mr={3}
            isLoading={isLoading}
            isDisabled={isApproved && (!detail || isLoadingSuggestion || Boolean(loadError))}
            p="20px"
          >
            {isApproved ? "Yes, verify it!" : "Yes, reject it!"}
          </Button>
          <Button onClick={onClose} isDisabled={isLoading} background="red" p="20px">
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default VerifyModal;
